/**
 * CategoryListContainer
 * --
 */
import React, { useEffect } from 'react';
import ProductCategoryListPresenter from './ProductCategoryListPresenter';
import {
  useCreateProductCategory,
  useUpdateProductCategory,
  useDeleteProductCategory,
  useGetProductCategories,
  useUpsertProductCategory,
  productCategoryQueryKeys,
} from 'hooks/ProductCategoryHooks';
import MessageAlert, { flattenTreeData, mappingTreeData } from 'utils';
import { useUserStack } from 'store';
import { useGetProducts } from 'hooks/ProductHooks';
import { useGetShopInformations } from 'hooks/ShopInformationHooks';
import { FileManager } from 'utils/FileManager';
import { useQueryClient } from '@tanstack/react-query';

const ProductCategoryListContainer = () => {
  /* ===== ROUTE ===== */
  /* ===== PROPS ===== */
  /* ===== MUTATE ===== */
  const { createProductCategory } = useCreateProductCategory();
  const { updateProductCategory } = useUpdateProductCategory();
  const { deleteProductCategory } = useDeleteProductCategory();
  const { upsertProductCategory } = useUpsertProductCategory();

  /* ===== DATA ===== */
  const queryClient = useQueryClient();
  const userInfo = useUserStack();
  // 상품
  const { data: Products } = useGetProducts(null, {
    select: (res) => {
      const { status, data } = res;

      if (status !== 200) return [];

      return data;
    },
    initialData: [],
  });
  // 상품 카테고리
  const { data: Categories } = useGetProductCategories(
    {
      type: 'PRODUCT',
    },
    {
      select: (res) => {
        const { status, data } = res;

        if (status !== 200) return [];

        return data;
      },
      initialData: [],
    }
  );
  // 입점업체
  const { data: Brand } = useGetShopInformations(null, {
    select: (res) => {
      const { status, data } = res;

      if (status !== 200) return [];

      return data;
    },
    initialData: [],
  });

  /* ===== STATE ===== */
  // 로딩
  const [isLoading, setIsLoading] = React.useState(false);
  // 정제된 데이터
  const [data, setData] = React.useState(null);

  // 모달
  const [isOpened, setIsOpened] = React.useState(false);
  const [modalType, setModalType] = React.useState(false);
  const [modalData, setModalData] = React.useState({
    menu: {},
    category: {},
  });
  /* ===== VARIABLE ===== */
  /* ===== FUNCTION ===== */
  /**
   * 모달 핸들러
   * --
   */
  const handleModal = (open, type = '', data = {}) => {
    setModalData((prev) => ({
      ...prev,
      [type]: data,
    }));

    setIsOpened(!open);
    setModalType(type);
  };

  /**
   * 데이터 바인딩 함수
   * --
   * @param {string} target
   * @param {*} value
   * @param {object} object
   * @param {*} callback
   */
  const handleChange = (t, v, o) => {
    let newData = modalData[o];

    newData[t] = v;
    const d = {
      ...modalData,
      o: { ...newData },
    };

    setModalData(d);
  };

  /**
   * 메뉴 삭제
   * --
   */
  const handleDeleteSelected = async (idx) => {
    if (!idx) {
      MessageAlert.warning('삭제할 항목을 선택해주세요.');
      return;
    }
    try {
      setIsLoading(true);

      const { status } = await deleteProductCategory(idx);

      if (status === 200) {
        const queryKey = productCategoryQueryKeys.getProductCategories({
          type: 'PRODUCT',
        });

        queryClient.invalidateQueries({
          queryKey: queryKey.queryKey,
        });

        MessageAlert.success('선택한 카테고리가 삭제되었습니다.');
      } else {
        MessageAlert.error(
          '카테고리와 적용된 다른 데이터를 확인하고 다시 시도해주세요.'
        );
      }
    } catch (err) {
      MessageAlert.error(
        '카테고리와 적용된 다른 데이터를 확인하고 다시 시도해주세요.'
      );
      console.error('[DELETE EVENT ERROR CATEGORY]:', err);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * 데이터 생성
   * --
   * @param {string} t
   * @param {string} s
   * @returns
   */
  const handleSubmit = async (t, s = 'CREATE', selectedItem = null) => {
    // 생성 타입 대문자 변환
    const type = t.toLocaleUpperCase();

    // 함수 타입 대문자 변환
    const submit = s.toLocaleUpperCase();

    // 생성 타입 정의
    const submitType = {
      CATEGORY: '카테고리',
      CREATE: '생성',
      UPDATE: '수정',
    };

    // NOTE: 데이터 유효성 검사 및 데이터 리턴
    const data = selectedItem
      ? await handleValidateCheck('UPDATECATE', selectedItem)
      : await handleValidateCheck(type);

    if (!data) return;

    // NOTE: 생성/수정 함수 정의
    const submitFunc = {
      CATEGORY: {
        CREATE: createProductCategory,
        UPDATE: updateProductCategory,
      },
    };

    try {
      // 로딩 처리
      setIsLoading(true);

      // 데이터 생성/수정
      const { status } = await submitFunc[type][submit]({
        ...data,
      });

      // }
      if (status === 200) {
        MessageAlert.success(
          `성공적으로 ${submitType[type]}이/가 ${submitType[submit]}되었습니다.`
        );

        if (submit === 'UPDATE') {
          const queryKeys = productCategoryQueryKeys.getProductCategories({
            type: 'PRODUCT',
          });

          // 캐싱된 데이터 업데이트
          queryClient.setQueryData(queryKeys.queryKey, (prev) => {
            const { data: oldData, ...rest } = prev;

            const newData = oldData?.map((category) =>
              category?.product_category_id === data?.product_category_id
                ? { ...category, ...data }
                : category
            );

            return {
              ...rest,
              data: newData,
            };
          });
        }

        setModalData((prev) => ({ ...prev, t: {} }));
        setIsOpened(false);
        return;
      }

      MessageAlert.warning(
        `${submitType[type]}을/를 ${submitType[submit]}하는데 실패하였습니다.`
      );
    } catch (err) {
      MessageAlert.error(
        `${submitType[type]}을/를 ${submitType[submit]}하는데 오류가 발생하였습니다.`
      );
      console.error(`[CREATE ${type}] ERROR : `, err);
      throw err;
    } finally {
      // 로딩 처리
      setTimeout(() => {
        setIsLoading(false);
      }, 500);
    }
  };

  /**
   * 유효성 검사
   * --
   * @param {string} t
   * @returns
   */
  const handleValidateCheck = async (t, selectedItem = null) => {
    if (t === 'CATEGORY') {
      // 카테고리 유효성 검사
      const { product_category_name, parents_id } = modalData?.category;

      if (!product_category_name) {
        MessageAlert.warning('카테고리명을 입력해주세요.');
        return false;
      }
      if (product_category_name?.length > 20) {
        MessageAlert.warning('카테고리명의 길이를 확인해주세요.');
        return false;
      }

      const findParents = FlattenCategories.filter(
        (category) => category?.parents_id === (parents_id ?? null)
      );

      return {
        ...modalData?.category,
        application_status: 'APPROVE',
        type: 'PRODUCT',
        order: findParents?.length + 1,
        // NOTE: 쇼브매지idx 추가
        shop_information_id: userInfo?.shop_information_id,
      };
    } else if (t === 'UPDATECATE') {
      // 카테고리 유효성 검사
      const { product_category_name, icon_url } = selectedItem;

      if (!product_category_name) {
        MessageAlert.warning('카테고리명을 입력해주세요.');
        return false;
      }
      if (product_category_name?.length > 20) {
        MessageAlert.warning('카테고리명의 길이를 확인해주세요.');
        return false;
      }

      // 아이콘 파일 업로드 시
      // 이미 등록된 파일을 수정하지 않았을 경우
      let fileUrl = null;
      if (icon_url && typeof icon_url === 'string') {
        fileUrl = icon_url;
      } else if (icon_url && typeof icon_url !== 'string') {
        const upload = await FileManager.upload('categories', icon_url);

        if (upload.status !== 200) {
          MessageAlert.error('아이콘 업로드에 실패하였습니다.');
          return false;
        }

        fileUrl = upload?.data?.file_name;
      }

      return {
        ...selectedItem,
        icon_url: fileUrl,
      };
    }
  };

  /**
   * 순서 변경
   * --
   */
  const handleUpdateOrder = async (newList) => {
    const confirm = window.confirm(`변경한 순서를 저장하시겠습니까?`);

    if (confirm) {
      try {
        setIsLoading(true);

        const newListData = newList.map((_item, _index) => ({
          ..._item,
          order: _index + 1,
        }));

        const { status } = await upsertProductCategory(newListData);

        if (status !== 200) {
          MessageAlert.error('일부 순서 변경에 실패했습니다.');
          return false;
        }

        setTimeout(() => {
          const queryKey = productCategoryQueryKeys.getProductCategories({
            type: 'PRODUCT',
          });

          queryClient.invalidateQueries({
            queryKey: queryKey.queryKey,
          });
          MessageAlert.success('순서가 변경되었습니다.');
        }, 500);
      } catch (err) {
        MessageAlert.error('순서 변경이 실패했습니다.');
        console.error('[UPDATE PRODUCT CATEGORY ERROR]:', err);
      } finally {
        setTimeout(() => {
          setIsLoading(false);
        }, 500);
      }
    }
  };

  /* ===== HOOKS ===== */
  useEffect(() => {
    if (!Categories?.data || !Products?.data) return; // 데이터가 없으면 실행하지 않음

    // const NewCategories = Categories?.data?.filter(
    //   (item) => item.application_status === 'APPROVE'
    // );
    // 상위 카테고리 및 하위 카테고리 그룹화
    const categoryMap = Categories?.data?.reduce((acc, category) => {
      if (!category.parents_id) {
        acc[category.product_category_id] = {
          ...category,
          key: category?.product_category_id, // key 추가
          title: category?.product_category_name, // title 추가
          children: [],
          menus: [], // 상위 카테고리의 메뉴 배열 초기화
        };
      } else {
        const parentCategory = acc[category.parents_id];
        if (parentCategory) {
          parentCategory.children.push({
            ...category,
            key: category?.product_category_id, // key 추가
            title: category?.product_category_name, // title 추가
            menus: [], // 하위 카테고리의 메뉴 배열 초기화
          });
        }
      }
      return acc;
    }, {});

    // 메뉴를 해당 카테고리 또는 하위 카테고리에 매핑
    Products.data.forEach((menu) => {
      if (menu.application_status !== 'APPROVE') return;
      const category = categoryMap[menu.product_category_id];
      if (category) {
        // 상위 카테고리의 메뉴 추가
        category.menus.push(menu);
      } else {
        // 하위 카테고리의 메뉴 추가
        const parentCategory = Object.values(categoryMap).find((c) =>
          c.children.some(
            (subCategory) =>
              subCategory.product_category_id === menu.product_category_id
          )
        );
        if (parentCategory) {
          const subCategory = parentCategory.children.find(
            (subCategory) =>
              subCategory.product_category_id === menu.product_category_id
          );
          if (subCategory) {
            subCategory.menus.push(menu);
          }
        }
      }
    });

    // 카테고리 데이터를 배열로 변환하여 업데이트
    const newData = Object.values(categoryMap)?.sort(
      (a, b) => (a.order || 0) - (b.order || 0)
    );

    // 하위 카테고리를 정렬
    Object.values(categoryMap).forEach((category) => {
      category.children.sort((a, b) => (a.order || 0) - (b.order || 0));
    });

    // 기존 데이터와 비교 후 상태 업데이트
    if (JSON.stringify(newData) !== JSON.stringify(data)) {
      setData(newData);
    }
  }, [Products, Categories?.data, data]);

  /**
   * 상품 카테고리 정제
   * --
   */
  const ProductCategories = React.useMemo(() => {
    if (!Categories?.length) return [];

    const newCategories = Categories.map((category) => ({
      ...category,
      title: category.product_category_name,
    }));

    const TreeData = mappingTreeData(
      newCategories,
      'product_category_id',
      'parents_id',
      'order'
    );

    return TreeData;
  }, [Categories]);

  /**
   * 상품 카테고리 플랫형 정제
   * --
   */
  const FlattenCategories = React.useMemo(() => {
    if (!ProductCategories?.length) return [];

    return flattenTreeData(ProductCategories);
  }, [ProductCategories]);

  /**
   * 상품 데이터 정제
   * --
   */
  const ProductList = React.useMemo(() => {
    if (!Products.length) return [];

    const newProducts = Products.map((product) => {
      const findShop = Brand?.find(
        (shop) => shop?.shop_information_id === product?.shop_information_id
      );

      const findCategory = FlattenCategories?.find(
        (category) =>
          category?.product_category_id === product?.product_category_id
      );

      return {
        ...product,
        brand: findShop?.shop_information_name,
        category: findCategory?.product_category_name,
        categoryKey: findCategory?.key,
      };
    });

    return newProducts;
  }, [Products, Brand, FlattenCategories]);

  /* ===== RENDER ===== */
  return (
    <>
      <ProductCategoryListPresenter
        categories={ProductCategories}
        data={ProductList}
        isLoading={isLoading}
        // categoriesOrder={categoriesOrder}
        modalData={modalData}
        modalType={modalType}
        isOpened={isOpened}
        onOpen={handleModal}
        onChange={handleChange}
        onSubmit={handleSubmit}
        onDelete={handleDeleteSelected}
        onUpdateOrder={handleUpdateOrder}
      />
    </>
  );
};

export default ProductCategoryListContainer;
